import {
    Collapse,
    DialogActions,
    DialogContent,
    InputAdornment,
    TextField,
    Unstable_Grid2 as Grid,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@/components/common/LoadingButton';
import { FormTextField } from '@/components/HookForm';
import { dialogClose, dialogOpen } from '@/modules/MuiDialog';
import { toast } from 'react-toastify';
import { patchMallBusinessInfo } from '@/services/mall.service';
import { useEffect, useMemo, useState } from 'react';
import { Check as ConfirmIcon } from '@mui/icons-material';
import { confirmVerifyPhoneCode, requestVerifyPhoneCode } from '@/services/open-mall.service';
import useInterval from '@/hooks/useInterval';
import DialogTitleBox from '@/modules/MuiDialog/DialogTitleBox';
import { useTranslation } from 'react-i18next';

interface ManagerPhoneDialogProps {
    mallId: string;
    managerPhoneNumber?: string;
}

export const ManagerPhoneDialog = (props: ManagerPhoneDialogProps) => {
    const { t } = useTranslation();
    const form = useForm({
        defaultValues: {
            managerPhoneNumber: props?.managerPhoneNumber || '',
        },
    });
    const {
        getValues,
        handleSubmit,
        formState: { isSubmitting },
    } = form;

    const [isConfirmedPhone, setIsConfirmedPhone] = useState<boolean>(false);
    const [isConfirmedCode, setIsConfirmedCode] = useState<boolean>(false);
    const [isWaitingPhoneCode, setIsWaitingPhoneCode] = useState<boolean>(false);
    const [phoneCode, setPhoneCode] = useState('');
    const [isWaitConfirmCode, setIsWaitConfirmCode] = useState(false);
    const [codeRequestCount, setCodeRequestCount] = useState(0);
    const [verifyLimitSec, setVerifyLimitSec] = useState(60);

    const displayLimitTime = useMemo(() => {
        const limitSec = verifyLimitSec;

        const h = Math.floor(limitSec / (60 * 60));
        const m = Math.floor((limitSec / 60) % 60);
        const s = limitSec % 60;

        const hh = h < 0 ? `${`${h}`.padStart(2, '0')}:` : '';
        const mm = `${m}`.padStart(2, '0');
        const ss = `${s}`.padStart(2, '0');

        return `${hh}${mm}:${ss}`;
    }, [verifyLimitSec]);

    function resetVerifyTimer() {
        setVerifyLimitSec(60);
    }

    useEffect(() => {
        resetVerifyTimer();
        setIsConfirmedCode(false);
    }, [codeRequestCount]);

    useInterval(() => {
        if (!codeRequestCount || !verifyLimitSec || isConfirmedCode) return;

        setVerifyLimitSec(verifyLimitSec - 1);
    }, 1000);

    async function formSubmit(data: any) {
        // 번호가 변경되지 않았으면 그냥 닫기
        if (props?.managerPhoneNumber === data.managerPhoneNumber) {
            dialogClose();
            return;
        }

        // 번호가 변경되었으면 인증 필수
        if (!isConfirmedPhone) {
            toast.error(t('mall.pleaseAuthenticateManagerContact'));
            return;
        }

        try {
            await patchMallBusinessInfo(props.mallId, {
                managerPhoneNumber: data.managerPhoneNumber,
            });
            toast.success(t('common.updated'));
            dialogClose();
        } catch (error) {
            toast.error(t('mall.businessInfoUpdateFailed'));
        }
    }

    return (
        <>
            <DialogTitleBox title={`${t('mall.managerContact')} ${t('common.edit')}`} />
            <DialogContent>
                <Grid container>
                    <Grid xs={12}>
                        <FormTextField
                            label={t('mall.managerContact')}
                            name={'managerPhoneNumber'}
                            form={form as any}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterManagerContact')}
                            fullWidth
                            required
                            rules={[
                                (v) => (!!v && !!v.trim()) || t('mall.requiredField'),
                                (v) =>
                                    /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/.test(v) ||
                                    t('mall.invalidPhoneNumberFormat'),
                            ]}
                            onChange={() => {
                                setIsConfirmedPhone(false);
                                setIsConfirmedCode(false);
                                setCodeRequestCount(0);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <LoadingButton
                                            variant="contained"
                                            isLoading={isWaitingPhoneCode}
                                            onClick={async (ev) => {
                                                ev.stopPropagation();
                                                ev.preventDefault();
                                                setIsWaitingPhoneCode(true);
                                                try {
                                                    const phoneNumber = getValues('managerPhoneNumber');
                                                    if (!phoneNumber) return;
                                                    await requestVerifyPhoneCode(phoneNumber);
                                                    resetVerifyTimer();
                                                    setPhoneCode('');
                                                    setCodeRequestCount(codeRequestCount + 1);
                                                    dialogOpen({
                                                        title: t('mall.verificationCodeSent'),
                                                        message: t('mall.verificationCodeSentMessage'),
                                                        closeText: t('common.close'),
                                                    });
                                                } catch (err: any) {
                                                    if (err?.data?.placeHolder || err?.placeHolder) {
                                                        toast.error(err?.data?.placeHolder || err?.placeHolder);
                                                    } else {
                                                        console.error(err);
                                                        toast.error(t('mall.verificationCodeRequestFailed'));
                                                    }
                                                } finally {
                                                    setIsWaitingPhoneCode(false);
                                                }
                                            }}
                                        >
                                            {t('mall.requestVerificationCode')}
                                        </LoadingButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Collapse in={codeRequestCount > 0}>
                            <TextField
                                label={t('mall.contactVerificationCode')}
                                value={phoneCode}
                                onChange={(e: any) => {
                                    setPhoneCode(e.target.value);
                                }}
                                error={verifyLimitSec <= 0 && !isConfirmedCode}
                                fullWidth
                                helperText={
                                    verifyLimitSec <= 0 && !isConfirmedCode ? t('mall.verificationTimeExpired') : ''
                                }
                                InputProps={{
                                    readOnly: isConfirmedCode,
                                    endAdornment: (
                                        <>
                                            <InputAdornment position="start">
                                                {isConfirmedCode ? '' : displayLimitTime}
                                            </InputAdornment>
                                            {!isConfirmedCode && (
                                                <InputAdornment position="end">
                                                    <LoadingButton
                                                        isLoading={isWaitConfirmCode}
                                                        variant="contained"
                                                        disabled={!(!!phoneCode && !!phoneCode.trim()) || isConfirmedCode}
                                                        onClick={async () => {
                                                            setIsWaitConfirmCode(true);
                                                            try {
                                                                const phoneNumber = getValues('managerPhoneNumber');
                                                                if (!phoneNumber) {
                                                                    toast.error(t('mall.phoneNumberInvalid'));
                                                                    return;
                                                                }
                                                                await confirmVerifyPhoneCode(phoneNumber, phoneCode);
                                                                toast.success(t('mall.contactVerificationCompleted'));
                                                                setIsConfirmedCode(true);
                                                                setIsConfirmedPhone(true);
                                                            } catch (err: any) {
                                                                if (err.data?.placeHolder || err.placeHolder) {
                                                                    toast.error(
                                                                        err.data?.placeHolder || err.placeHolder,
                                                                    );
                                                                } else {
                                                                    console.error(err);
                                                                    toast.error(
                                                                        t('mall.verificationCodeConfirmationFailed'),
                                                                    );
                                                                }
                                                            } finally {
                                                                setIsWaitConfirmCode(false);
                                                            }
                                                        }}
                                                    >
                                                        {t('mall.confirmVerificationCode')}
                                                    </LoadingButton>
                                                </InputAdornment>
                                            )}
                                            {isConfirmedCode && <ConfirmIcon color={'success'} />}
                                        </>
                                    ),
                                }}
                            />
                        </Collapse>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    isLoading={isSubmitting}
                    onClick={async () => {
                        await handleSubmit(formSubmit)();
                    }}
                >
                    {t('common.edit')}
                </LoadingButton>
                <LoadingButton
                    color={'error'}
                    onClick={() => {
                        dialogClose();
                    }}
                >
                    {t('common.cancel')}
                </LoadingButton>
            </DialogActions>
        </>
    );
};
